import React, { useState, useEffect, useCallback } from "react";
import { makeStyles, Paper, Typography, Modal, Box, alpha } from "@material-ui/core";
import MainContainer from "../../components/MainContainer";
import HelpOutlineIcon from "@material-ui/icons/HelpOutline";
import PlayCircleFilledIcon from "@material-ui/icons/PlayCircleFilled";
import { i18n } from "../../translate/i18n";
import useHelps from "../../hooks/useHelps";

const useStyles = makeStyles(theme => ({
  mainContainer: {
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)"
      : "linear-gradient(135deg, #f5f7fa 0%, #e4e8ec 100%)",
    minHeight: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(3),
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.6)
      : alpha("#fff", 0.8),
    backdropFilter: "blur(10px)",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.08)}`,
    borderRadius: "16px 16px 0 0",
    marginBottom: theme.spacing(2),
  },
  titleIcon: {
    width: 48,
    height: 48,
    borderRadius: 12,
    background: `linear-gradient(135deg, #673ab7 0%, #512da8 100%)`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    boxShadow: `0 6px 20px ${alpha("#673ab7", 0.35)}`,
    "& svg": {
      color: "#fff",
      fontSize: 24,
    },
  },
  titleWrapper: {
    display: "flex",
    flexDirection: "column",
  },
  titleText: {
    fontWeight: 700,
    fontSize: "1.5rem",
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #fff 0%, #ccc 100%)"
      : "linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
  },
  titleCount: {
    color: theme.palette.text.secondary,
    fontSize: "0.9rem",
  },
  mainPaperContainer: {
    flex: 1,
    overflowY: 'auto',
    padding: theme.spacing(2),
    ...theme.scrollbarStyles,
  },
  mainPaper: {
    width: '100%',
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
    gap: theme.spacing(3),
    padding: theme.spacing(1),
  },
  helpPaper: {
    position: 'relative',
    width: '100%',
    minHeight: '280px',
    borderRadius: 16,
    overflow: 'hidden',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    background: theme.palette.type === "dark"
      ? alpha(theme.palette.background.paper, 0.8)
      : theme.palette.background.paper,
    backdropFilter: "blur(10px)",
    border: `1px solid ${theme.palette.type === "dark" ? alpha("#fff", 0.1) : alpha("#000", 0.05)}`,
    boxShadow: theme.palette.type === "dark"
      ? "0 8px 32px rgba(0, 0, 0, 0.3)"
      : "0 4px 20px rgba(0, 0, 0, 0.08)",
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-8px)',
      boxShadow: theme.palette.type === "dark"
        ? `0 16px 48px rgba(0, 0, 0, 0.5), 0 0 0 2px ${theme.palette.primary.main}`
        : `0 12px 40px rgba(0, 0, 0, 0.15), 0 0 0 2px ${theme.palette.primary.main}`,
      "& $playOverlay": {
        opacity: 1,
      },
      "& $videoThumbnail": {
        transform: 'scale(1.05)',
      },
    },
  },
  thumbnailContainer: {
    position: 'relative',
    width: '100%',
    height: 180,
    overflow: 'hidden',
  },
  videoThumbnail: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    transition: 'transform 0.3s ease',
  },
  playOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.4)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    opacity: 0,
    transition: 'opacity 0.3s ease',
    "& svg": {
      fontSize: 64,
      color: '#fff',
      filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.3))',
    },
  },
  cardContent: {
    padding: theme.spacing(2),
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
  },
  videoTitle: {
    fontWeight: 600,
    color: theme.palette.text.primary,
    fontSize: '1rem',
    marginBottom: theme.spacing(1),
    lineHeight: 1.3,
  },
  videoDescription: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    overflow: 'hidden',
    display: '-webkit-box',
    WebkitLineClamp: 3,
    WebkitBoxOrient: 'vertical',
    lineHeight: 1.5,
  },
  videoModal: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  videoModalContent: {
    outline: 'none',
    width: '90%',
    maxWidth: 1024,
    aspectRatio: '16/9',
    position: 'relative',
    backgroundColor: '#000',
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(0,0,0,0.5)',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(8),
    color: theme.palette.text.secondary,
    "& svg": {
      fontSize: 80,
      marginBottom: theme.spacing(2),
      opacity: 0.5,
    },
  },
}));

const Helps = () => {
  const classes = useStyles();
  const [records, setRecords] = useState([]);
  const { list } = useHelps();
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    async function fetchData() {
      const helps = await list();
      setRecords(helps);
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openVideoModal = (video) => {
    setSelectedVideo(video);
  };

  const closeVideoModal = () => {
    setSelectedVideo(null);
  };

  const handleModalClose = useCallback((event) => {
    if (event.key === "Escape") {
      closeVideoModal();
    }
  }, []);

  useEffect(() => {
    document.addEventListener("keydown", handleModalClose);
    return () => {
      document.removeEventListener("keydown", handleModalClose);
    };
  }, [handleModalClose]);

  const renderVideoModal = () => {
    return (
      <Modal
        open={Boolean(selectedVideo)}
        onClose={closeVideoModal}
        className={classes.videoModal}
      >
        <div className={classes.videoModalContent}>
          {selectedVideo && (
            <iframe
              style={{ width: "100%", height: "100%", position: "absolute", top: 0, left: 0 }}
              src={`https://www.youtube.com/embed/${selectedVideo}?autoplay=1`}
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          )}
        </div>
      </Modal>
    );
  };

  const renderHelps = () => {
    if (!records.length) {
      return (
        <Box className={classes.emptyState}>
          <HelpOutlineIcon />
          <Typography variant="h6">
            Nenhum vídeo de ajuda disponível
          </Typography>
          <Typography variant="body2">
            Os vídeos tutoriais serão exibidos aqui
          </Typography>
        </Box>
      );
    }

    return (
      <div className={classes.mainPaper}>
        {records.map((record, key) => (
          <Paper 
            key={key} 
            className={classes.helpPaper} 
            onClick={() => openVideoModal(record.video)}
          >
            <Box className={classes.thumbnailContainer}>
              <img
                src={`https://img.youtube.com/vi/${record.video}/mqdefault.jpg`}
                alt="Thumbnail"
                className={classes.videoThumbnail}
              />
              <Box className={classes.playOverlay}>
                <PlayCircleFilledIcon />
              </Box>
            </Box>
            <Box className={classes.cardContent}>
              <Typography className={classes.videoTitle}>
                {record.title}
              </Typography>
              <Typography className={classes.videoDescription}>
                {record.description}
              </Typography>
            </Box>
          </Paper>
        ))}
      </div>
    );
  };

  return (
    <MainContainer className={classes.mainContainer}>
      {/* Modern Header */}
      <Box className={classes.header}>
        <Box className={classes.titleIcon}>
          <HelpOutlineIcon />
        </Box>
        <Box className={classes.titleWrapper}>
          <Typography className={classes.titleText}>
            {i18n.t("helps.title")}
          </Typography>
          <Typography className={classes.titleCount}>
            {records.length} vídeos disponíveis
          </Typography>
        </Box>
      </Box>
      
      <Box className={classes.mainPaperContainer}>
        {renderHelps()}
      </Box>
      {renderVideoModal()}
    </MainContainer>
  );
};

export default Helps;
